import type { Round } from '@uefadigital/panenka';
import { MatchStatus, RoundMode } from '@uefadigital/panenka';
import { PkCarousel, PkDataCard, PkDivider } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { Fragment, memo, useMemo } from 'react';

import { useDrawPots } from '../../hooks';
import { useDrawOptions, useDrawStore } from '../../store';
import DrawTeam from '../Team';

import { useHash } from '@hooks/useHash';
import { hydrateWithTranslations } from '@services/TranslatorService';
import { useWindowSize } from '@shared/hooks/useWindowSize';
import { useTranslateFn } from '@shared/Translate';
import type { DrawPot } from '@shared-types/draw.types';

import * as styles from './styles.module.scss';

const breakpoints = {
  '0': {
    slidesPerView: 1.4,
    spaceBetween: 12,
  },
  '768': {
    slidesPerView: 2.1,
    spaceBetween: 16,
  },
  '1024': {
    slidesPerView: 3.1,
    slidesPerGroup: 3,
    spaceBetween: 16,
  },
  '1440': {
    slidesPerView: 4,
    spaceBetween: 24,
  },
} as const;

const breakpoints2 = {
  '0': {
    slidesPerView: 1.4,
    spaceBetween: 16,
  },
  '768': {
    slidesPerView: 2,
    spaceBetween: 16,
  },
} as const;

const breakpoints3 = {
  '0': {
    slidesPerView: 1.4,
    spaceBetween: 32,
  },
  '768': {
    slidesPerView: 2.4,
    spaceBetween: 16,
  },
  '1024': {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 16,
  },
} as const;

const breakpoints4 = {
  '0': {
    slidesPerView: 1.1,
    spaceBetween: 12,
  },
  '768': {
    slidesPerView: 2.1,
    spaceBetween: 16,
  },
  '1024': {
    slidesPerView: 3.1,
    slidesPerGroup: 3,
    spaceBetween: 16,
  },
  '1440': {
    slidesPerView: 4,
    spaceBetween: 24,
  },
} as const;

const breakpointsMap = {
  4: breakpoints4,
  3: breakpoints3,
  2: breakpoints2,
  default: breakpoints,
} as const;

const SingleDrawPots = ({
  pots,
  round,
  isMultiRound,
  drawHasVideo,
}: {
  pots: DrawPot[];
  round: Round;
  isMultiRound: boolean;
  drawHasVideo?: boolean;
}) => (
  <div
    className={clsx(
      styles.singlePotWrapper,
      pots.length === 1 && styles.onePot,
      !drawHasVideo && styles.singlePotNoVideo,
    )}
  >
    {isMultiRound && <h3 className={styles.potsGroupTitle}>{hydrateWithTranslations(round)?.name}</h3>}
    {pots?.map((pot) => (
      <div key={pot.potId}>
        <PkDataCard className={styles.singlePot} rounded='xl' stroke={false}>
          {pots.length > 1 && (
            <h3 slot='header' className={styles.potTitle}>
              {hydrateWithTranslations(pot)?.name}
            </h3>
          )}

          <div slot='body' className={clsx('pk-d--flex pk-flex--column pk-gap--xs2', styles.singlePotBody)}>
            {pot.teamIds?.map((id, index) => (
              <Fragment key={id}>
                <DrawTeam team={pot.teams.find((x) => x.id === id)} />
                {index !== pot.teams.length - 1 && <PkDivider className={styles.potsDivider} />}
              </Fragment>
            ))}
          </div>
        </PkDataCard>
      </div>
    ))}
  </div>
);

const DrawPotsCarousel = ({ pots, round, isMultiRound }: { pots: DrawPot[]; round: Round; isMultiRound: boolean }) => {
  const t = useTranslateFn();
  const carouselBreackpoints = useMemo(() => breakpointsMap[pots.length] ?? breakpointsMap.default, [pots.length]);
  const {
    options: { isPotsTab },
  } = useDrawOptions();

  return (
    <PkCarousel
      className={styles.carousel}
      boxShadowPadding
      key={pots.map((p) => p.potId).join('-')}
      breakpoints={carouselBreackpoints}
      observer
      observeSlideChildren
      resizeObserver
      autoHeight
    >
      {isMultiRound ? (
        <h3 slot='headline' className={styles.potsGroupTitle}>
          {hydrateWithTranslations(round)?.name}
        </h3>
      ) : (
        !isPotsTab && (
          <h2 slot='headline' className={styles.potsTitle}>
            {t(window?.isClub ? 'clubs_in_the_draw' : 'teams_in_the_draw')}
          </h2>
        )
      )}
      {pots
        .filter((pot) => pot.isVisible)
        .map((pot) => (
          <PkDataCard key={pot.potId} className={styles.multiplePots} rounded='xl' stroke={false}>
            <h3 slot='header' className={styles.potTitle}>
              {hydrateWithTranslations(pot)?.name}
            </h3>
            <div
              className={clsx(
                'pk-d--flex pk-flex--column pk-gap--xs2',
                styles.multiplePotsBody,
                pots.length === 2 && styles.multiplePotsBody,
              )}
              slot='body'
            >
              {pot.teamIds?.map((id, index) => (
                <Fragment key={id}>
                  <DrawTeam team={pot.teams.find((x) => x.id === id)} />
                  {index !== pot.teams.length - 1 && <PkDivider className={styles.potsDivider} />}
                </Fragment>
              ))}
            </div>
          </PkDataCard>
        ))}
    </PkCarousel>
  );
};

const DrawPots = () => {
  const { filteredPots: potsGroups } = useDrawPots();
  const isMultiRound = potsGroups?.length > 1;
  const { isDesktop } = useWindowSize();
  const draw = useDrawStore((state) => state.draw);

  const potsHash = useHash(potsGroups);

  const drawHasVideo = useMemo(
    () =>
      (draw?.status?.videoStreamAvailable || draw?.status?.eventStatus === MatchStatus.FINISHED) && !!draw?.videoPath,
    [draw?.status?.eventStatus, draw?.status?.videoStreamAvailable, draw?.videoPath],
  );

  const renderComponent = (length: number, pots: DrawPot[], round: Round) => {
    if ((length === 4 || length === 5) && round.mode === RoundMode.GROUP) {
      return drawHasVideo && isDesktop ? (
        <SingleDrawPots pots={pots} isMultiRound={isMultiRound} round={round} drawHasVideo={drawHasVideo} />
      ) : (
        <DrawPotsCarousel pots={pots} isMultiRound={isMultiRound} round={round} />
      );
    }
    return <SingleDrawPots pots={pots} isMultiRound={isMultiRound} round={round} drawHasVideo={drawHasVideo} />;
  };

  return (
    <div className={styles.potsWrapper}>
      {potsGroups?.map((potsGroup) => (
        <div className={styles.potsGroup} key={potsHash}>
          {renderComponent(potsGroup.pots.length, potsGroup.pots, potsGroup.round)}
        </div>
      ))}
    </div>
  );
};

export default memo(DrawPots);
